/**
 * Générateur — Automatisme : diviseurs, multiples et tables (réactivation)
 * Attendu ID : 1013 | Niveau : 6ème | Domaine : NC
 * Rubrique : AUTOMATISME (programme 2025) — réactivation d'acquis CM2 en
 * vue du calcul sur les fractions (la finalité, PAS le contenu : aucune
 * addition/soustraction de fractions ici, autre automatisme).
 * Labels (cartes exerciseur) :
 *   - Diviseurs, multiples et tables
 *
 * ── Plan de cartes (2026-08-20, spec prof) ─────────────────────────────────
 * UNE carte, 5 formats = les 5 types d'items AUTORISÉS, avec les BORNES du
 * programme de CM2 verrouillées par des gardes (throw) :
 *   - citer les premiers MULTIPLES d'un nombre (tables : n ∈ [2..10]) ;
 *   - déterminer les diviseurs (QCM multiple « coche », deux régimes mêlés
 *     2026-08-21 : liste complète si N ≤ 30, candidats tous ≤ 10 si
 *     31 ≤ N ≤ 100 — « des diviseurs d'un nombre ≤ 100 ») ;
 *   - TESTER multiple/diviseur (diviseur ≤ 10, nombre ≤ 100) en V/F ;
 *   - DIVISEURS COMMUNS de deux nombres ≤ 30 (QCM à erreurs nommées) ;
 *   - MULTIPLES COMMUNS de deux nombres < 15 (QCM reconnaissance : « le plus
 *     petit multiple commun » écarté le 2026-08-21, PPCM hors cycle 3).
 * INTERDITS respectés : jamais « Simplifie », pas de PGCD, pas de fraction
 * irréductible, pas d'opération de fractions. Les divisions par 2, 5 et 10
 * (seuls CRITÈRES au programme) sont PRIVILÉGIÉES (~moitié des tests) ; les
 * autres tables restent possibles mais parcimonieuses.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1013;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Diviseurs, multiples et tables';

// ─── Outils arithmétiques (et gardes de bornes) ────────────────────────────

const diviseursDe = (N) => {
    const res = [];
    for (let d = 1; d <= N; d++) if (N % d === 0) res.push(d);
    return res;
};

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1013 : ${message}`);
};

/** Critères au programme (2, 5, 10) privilégiés ; autres tables parcimonie. */
const POOL_DIVISEURS_TEST = [2, 2, 5, 5, 10, 10, 3, 4, 6, 7, 8, 9];

// ─── Format 1 : les premiers multiples (texte à trous, saisie) ────────────

const genMultiplesTrouSaisie = () => {
    const n = randInt(2, 10);
    garde(n >= 2 && n <= 10, 'table hors bornes');
    // Suite de 6 multiples consécutifs, DÉPART ALÉATOIRE dans la table
    // (décision prof 2026-08-21 : évite la récitation du début de table, le
    // geste reste « compter de n en n depuis le terme affiché »). Plafond :
    // dernier terme ≤ 100 (borne du travail sur les multiples au CM2).
    // Le premier terme est donné, 4 trous parmi les 5 suivants (un terme en
    // clair sert de point de contrôle). Chaque trou a une valeur UNIQUE
    // (suite strictement croissante) : aucun échange de réponses possible.
    const debutMax = Math.min(5, Math.floor(100 / n) - 5);
    const debut = randInt(1, Math.max(1, debutMax));
    const rangs = [0, 1, 2, 3, 4, 5].map((k) => (debut + k) * n);
    garde(rangs[5] <= 100, 'multiple au-delà de 100');
    const trous = new Set(shuffle([1, 2, 3, 4, 5]).slice(0, 4));
    const suite = rangs.map((v, i) => (trous.has(i) ? `{{${v}}}` : String(v))).join(' ; ');
    return makeTexteTrou(
        suite,
        `<p><b>Complète la suite des multiples de ${n}, rangés du plus petit au plus grand.</b></p>`,
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : TOUS les diviseurs (QCM multiple « coche ») ───────────────

/** Nombres ≤ 30 à liste de diviseurs intéressante (4 à 6 diviseurs). */
const NOMBRES_DIVISEURS = [6, 8, 10, 12, 14, 15, 16, 18, 20, 21, 22, 26, 27, 28];

/** Régime grand (31..100) : les nombres ayant ≥ 2 diviseurs LISIBLES dans la
 *  table de Pythagore (N ≤ 10 × d), pour que chaque case soit décidable par
 *  un élève de 6e (tables + critères 2/5/10, jamais de division cachée du
 *  type 98 = 2 × 49). */
const NOMBRES_GRANDS = [32, 35, 36, 40, 42, 45, 48, 50, 54, 56, 60, 63, 70, 72, 80, 90];

/** Deux régimes mêlés (décision prof 2026-08-21), consigne unifiée « Coche
 *  les diviseurs de N » (le « TOUS » ne teste rien de plus en grille : les
 *  candidats sont sous les yeux de l'élève, son geste est de tester chacun) :
 *  - N ≤ 30 : la grille contient la liste COMPLÈTE des diviseurs + leurres ;
 *  - 31 ≤ N ≤ 100 (« des diviseurs d'un nombre ≤ 100 ») : les 8 candidats
 *    sont TOUS pris entre 1 et 10 — outillage CM2 : critères 2/5/10, tables,
 *    petite division ; un diviseur > 10 (12 pour 72...) serait hors
 *    outillage, il est écarté. La grille contient tous les diviseurs ≤ 10
 *    de N (2 à 7 selon N, jamais moins de 2 : premiers exclus). */
const genDiviseursQcmMultiple = () => {
    // Poids 30/70 (décision prof 2026-08-21) : le régime petit n'a que 14
    // questions, à 50 % il revenait trop souvent en session.
    const regimeGrand = Math.random() < 0.7;
    for (let essai = 0; essai < 60; essai++) {
        let N; let divs; let nonDivs;
        if (regimeGrand) {
            // Correction prof 2026-08-21 (vécu « diviseurs de 98 ») : tout
            // doit rester DÉCIDABLE avec l'outillage CM2. N est restreint aux
            // nombres de 31 à 100 ayant ≥ 2 diviseurs lisibles dans la table
            // de Pythagore, et un diviseur vrai n'entre dans la grille que si
            // N est dans sa table (N ≤ 10 × d) OU si son critère est au
            // programme (2, 5, 10). Ex. : pour 54, jamais 3 (54 > 30 et pas
            // de critère par 3) ; 7 pour 98 aurait été indécidable → 98 exclu.
            N = randomChoice(NOMBRES_GRANDS);
            garde(N <= 100, '« des diviseurs » exige N ≤ 100');
            divs = [];
            for (let d = 1; d <= 10; d++) {
                if (N % d !== 0) continue;
                if (d === 1 || [2, 5, 10].includes(d) || N <= 10 * d) divs.push(d);
            }
            garde(divs.length >= 2, 'régime grand : moins de 2 diviseurs décidables');
            const pool = [];
            for (let c = 1; c <= 10; c++) if (N % c !== 0) pool.push(c);
            nonDivs = shuffle(pool).slice(0, 8 - divs.length);
        } else {
            N = randomChoice(NOMBRES_DIVISEURS);
            garde(N <= 30, 'liste complète : N ≤ 30');
            divs = diviseursDe(N);
            if (divs.length > 6) continue;
            // Cas N = 6 (validé prof 2026-08-21) : seuls 4 et 5 existent
            // comme leurres → grille réduite à 6 cases, seul cas sous les 8.
            const pool = [];
            for (let c = 2; c < N; c++) if (N % c !== 0) pool.push(c);
            nonDivs = shuffle(pool).slice(0, 8 - divs.length);
            if (nonDivs.length < 2) continue;
        }
        const choix = shuffle([...divs, ...nonDivs]).map(String);
        const payload = makeQcm(
            choix,
            divs.map(String),
            `<p><b>Coche les diviseurs de ${N}.</b></p>`,
            opts(L1),
            true,
        );
        payload._meta.params = { N, regime: regimeGrand ? 'grand' : 'petit', diviseurs: divs, leurres: nonDivs };
        return payload;
    }
    const divs = diviseursDe(18);
    const payload = makeQcm(
        [...divs.map(String), '4', '5'],
        divs.map(String),
        '<p><b>Coche les diviseurs de 18.</b></p>',
        opts(L1),
        true,
    );
    payload._meta.params = { N: 18, regime: 'petit', diviseurs: divs, leurres: [4, 5] };
    return payload;
};

// ─── Format 3 : tester multiple / diviseur (V/F) ──────────────────────────

const genTestsVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const lignes = [];
        const verites = [];
        const vus = new Set();
        let criteres = 0;
        for (let t = 0; t < 60 && lignes.length < 4; t++) {
            const d = randomChoice(POOL_DIVISEURS_TEST);
            garde(d <= 10, 'diviseur de test > 10');
            const vrai = Math.random() < 0.5;
            let X;
            if (vrai) X = d * randInt(2, Math.floor(100 / d));
            else {
                X = randInt(11, 100);
                if (X % d === 0) X += 1;
                if (X > 100 || X % d === 0) continue;
            }
            const enonce = Math.random() < 0.5
                ? `${X} est un multiple de ${d}`
                : `${d} est un diviseur de ${X}`;
            // Dédup sur le FAIT (X, d), pas sur la phrase : « 35 est un
            // multiple de 5 » et « 5 est un diviseur de 35 » sont la même
            // question sous deux formulations (revue 2026-08-21, 0,7 %).
            if (vus.has(`${X}|${d}`)) continue;
            // Vérité RE-CALCULÉE (jamais l'intention) :
            const verite = X % d === 0;
            vus.add(`${X}|${d}`);
            lignes.push(enonce);
            verites.push(verite);
            if ([2, 5, 10].includes(d)) criteres++;
        }
        const nbVraies = verites.filter(Boolean).length;
        if (lignes.length !== 4 || nbVraies < 1 || nbVraies > 3 || criteres < 2) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        ['35 est un multiple de 5', '2 est un diviseur de 47',
            '70 est un multiple de 10', '7 est un diviseur de 42'],
        [0, 1, 0, 0],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 4 : diviseur commun (QCM à erreurs nommées) ───────────────────

const genCommunsDiviseursQcm = () => {
    // Revue 2026-08-21 : sans biais, « 2 » était la bonne réponse dans 58 %
    // des tirages (la plupart des paires n'ont que 2 en commun). Deux crans :
    // une paire RICHE (≥ 2 diviseurs communs > 1) est exigée une fois sur
    // deux, et parmi plusieurs communs on préfère le plus grand 2 fois sur 3.
    const exigeRiche = Math.random() < 0.5;
    for (let essai = 0; essai < 60; essai++) {
        const a = randInt(6, 30);
        const b = randInt(6, 30);
        garde(a <= 30 && b <= 30, 'diviseurs communs : nombres ≤ 30');
        if (a === b) continue;
        const divsA = diviseursDe(a).filter((d) => d > 1);
        const divsB = diviseursDe(b).filter((d) => d > 1);
        const communs = divsA.filter((d) => divsB.includes(d));
        const seulA = divsA.filter((d) => !divsB.includes(d));
        const seulB = divsB.filter((d) => !divsA.includes(d));
        if (communs.length === 0 || seulA.length === 0 || seulB.length === 0) continue;
        if (exigeRiche && communs.length < 2 && essai < 40) continue;    // repli après 40 essais
        const bonne = String(communs.length > 1 && Math.random() < 2 / 3
            ? Math.max(...communs)
            : randomChoice(communs));
        let aucun = null;
        for (let t = 0; t < 50 && aucun === null; t++) {
            const c = randInt(2, 12);
            if (a % c !== 0 && b % c !== 0) aucun = c;
        }
        if (aucun === null) continue;
        const candidats = [
            { valeur: String(randomChoice(seulA)), code: 'diviseur_de_a_seulement' },
            { valeur: String(randomChoice(seulB)), code: 'diviseur_de_b_seulement' },
            { valeur: String(aucun), code: 'diviseur_d_aucun' },
        ];
        const choix = [bonne, ...candidats.map((c) => c.valeur)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(
            choix,
            bonne,
            `<p><b>Quel nombre est un diviseur commun de ${a} et ${b} ?</b></p>`,
            opts(L1),
            false,
        );
        payload._meta.params = { a, b, distracteurs: candidats };
        return payload;
    }
    const payload = makeQcm(['6', '4', '9', '5'], '6',
        '<p><b>Quel nombre est un diviseur commun de 12 et 18 ?</b></p>', opts(L1), false);
    payload._meta.params = { a: 12, b: 18, distracteurs: [] };
    return payload;
};

// ─── Format 5 : multiple commun (QCM reconnaissance, décision prof
//     2026-08-21 — « le plus petit multiple commun » écarté : le PPCM
//     n'existe pas au cycle 3, on reste sur « déterminer DES multiples
//     communs ». Bonne réponse ≤ 100 : les tables suffisent, les paires
//     laborieuses (11 et 13...) s'excluent d'elles-mêmes) ─────────────────

const pgcdInterne = (x, y) => (y === 0 ? x : pgcdInterne(y, x % y));

const genCommunsMultiplesQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(2, 14);
        const b = randInt(2, 14);
        garde(a < 15 && b < 15, 'multiples communs : nombres < 15');
        if (a === b || a % b === 0 || b % a === 0) continue;   // trivial sinon
        const ppmc = (a * b) / pgcdInterne(a, b);
        const bonneVal = ppmc * randomChoice(ppmc * 2 <= 100 ? [1, 1, 2] : [1]);
        if (bonneVal > 100) continue;
        // multiple de a seulement / de b seulement (dans les tables, ≤ 100)
        const multipleSeul = (x, autre) => {
            for (let t = 0; t < 50; t++) {
                const m = x * randInt(1, Math.floor(100 / x));
                if (m % autre !== 0 && m !== bonneVal) return m;
            }
            return null;
        };
        const seulA = multipleSeul(a, b);
        const seulB = multipleSeul(b, a);
        let aucun = null;
        for (let t = 0; t < 50 && aucun === null; t++) {
            const c = randInt(2, 100);
            if (c % a !== 0 && c % b !== 0) aucun = c;
        }
        if (seulA === null || seulB === null || aucun === null) continue;
        const candidats = [
            { valeur: String(seulA), code: 'multiple_de_a_seulement' },
            { valeur: String(seulB), code: 'multiple_de_b_seulement' },
            { valeur: String(aucun), code: 'multiple_d_aucun' },
        ];
        const choix = [String(bonneVal), ...candidats.map((c) => c.valeur)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(
            choix,
            String(bonneVal),
            `<p><b>Quel nombre est un multiple commun de ${a} et ${b} ?</b></p>`,
            opts(L1),
            false,
        );
        payload._meta.params = { a, b, distracteurs: candidats };
        return payload;
    }
    const payload = makeQcm(['12', '8', '18', '10'], '12',
        '<p><b>Quel nombre est un multiple commun de 4 et 6 ?</b></p>', opts(L1), false);
    payload._meta.params = { a: 4, b: 6, distracteurs: [] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'bornes du programme GARDÉES par des throw : tous-les-diviseurs ≤ 30, diviseurs communs ≤ 30, multiples communs < 15, testeur ≤ 10',
        'jamais « Simplifie », jamais PGCD/irréductible, jamais d\'opération de fractions (finalité ≠ contenu)',
    ],
    divmult_multiples_communs_qcm: {
        support: 'deux nombres < 15 (ni égaux ni l\'un multiple de l\'autre), bonne réponse = un multiple commun ≤ 100',
        fixes: {
            multiple_de_a_seulement: 'multiple du premier nombre mais pas du second',
            multiple_de_b_seulement: 'multiple du second mais pas du premier',
            multiple_d_aucun: 'multiple ni de l\'un ni de l\'autre',
        },
    },
    divmult_communs_qcm: {
        support: 'deux nombres ≤ 30 ayant un diviseur commun > 1 (et chacun un diviseur propre)',
        fixes: {
            diviseur_de_a_seulement: 'divise le premier nombre mais pas le second',
            diviseur_de_b_seulement: 'divise le second mais pas le premier',
            diviseur_d_aucun: 'ne divise ni l\'un ni l\'autre',
        },
    },
    divmult_diviseurs_qcm_multiple: {
        support: '« coche les diviseurs de N », deux régimes mêlés : N ≤ 30 (liste complète des diviseurs dans la grille + non-diviseurs < N) ; régime grand (16 nombres de 31 à 100 lisibles dans la table de Pythagore) : candidats TOUS ≤ 10 et TOUS décidables (N dans la table du diviseur, ou critère 2/5/10) — jamais de division cachée type 98 = 2 × 49',
        fixes: { non_diviseur: 'un candidat du même régime qui ne divise pas N' },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    divmult_multiples_texte_trou_saisie: genMultiplesTrouSaisie,
    divmult_diviseurs_qcm_multiple: genDiviseursQcmMultiple,
    divmult_tests_tableau_vf: genTestsVraiFaux,
    divmult_communs_qcm_unique: genCommunsDiviseursQcm,
    divmult_multiples_communs_qcm: genCommunsMultiplesQcm,
};

const FORMAT_LABELS = {
    multiples_texte_trou_saisie: 'Multiples',
    diviseurs_qcm_multiple: 'Diviseurs',
    tests_tableau_vf: 'Vrai/Faux',
    communs_qcm_unique: 'Div. communs',
    multiples_communs_qcm: 'Mult. communs',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'divmult', label: L1, formatsList: buildFormats('divmult') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
